<template>
  <div class="change-work-info-wrapper">
    <div class="change-work-info-container">
      <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
      <div class="back-block" @click="$router.push({ name: 'settings' })">
        <img
          src="../../../../public/custom/images/back-arrow.svg"
          class="back-img"
          alt="Back arrow"
        />
        <span class="back-btn-text">{{ $t('message.loanPage.back') }}</span>
      </div>
      <div v-if="isBorrower" class="work-info-notice">
        {{ $t('message.changeWorkInfoPage.borrowerNotice') }}
      </div>
      <div class="info-section">
        <div class="section-title">{{ $t('message.changeWorkInfoPage.title') }}</div>
        <div v-if="!workInfoView.hasData" class="empty-text">
          {{ $t('message.changeWorkInfoPage.empty') }}
        </div>
        <div v-else class="fields-list">
          <div
            v-for="field in workInfoView.fields"
            :key="field.key"
            class="field-row"
          >
            <div class="field-label">{{ $t(field.labelKey) }}</div>
            <div class="field-value">{{ field.value }}</div>
          </div>
        </div>
      </div>
      <div v-if="isBorrower" class="info-section">
        <div class="section-title">{{ $t('message.changeWorkInfoPage.extraTitle') }}</div>
        <div v-if="!extraUserInfoView.hasData" class="empty-text">
          {{ $t('message.changeWorkInfoPage.extraEmpty') }}
        </div>
        <div v-else class="fields-list">
          <div
            v-for="field in extraUserInfoView.fields"
            :key="field.key"
            class="field-row"
          >
            <div class="field-label">{{ $t(field.labelKey) }}</div>
            <div class="field-value">{{ field.value }}</div>
          </div>
        </div>
      </div>
      <div class="edit-actions">
        <div class="edit-btn-container">
          <base-button
            :text="$t('message.changeWorkInfoPage.editBtn')"
            :min-height="'38px'"
            :disabled="!isEditAvailable"
            @click="goToEdit()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '../../../components/base/BaseButton.vue';
import { getWorkInfoView } from '../../../helpers/workInfo';
import { getExtraUserInfoView } from '../../../helpers/extraUserInfo';

export default {
  name: 'ChangeWorkInfo',
  components: {
    BaseButton
  },
  async created() {
    await this.$store.dispatch('getProfile');
  },
  computed: {
    ...mapGetters(['profile']),
    isBorrower() {
      return this.profile?.role === 'borrower';
    },
    expiredCreditRegisterAgreement() {
      return !!this.profile?.expiredCreditRegisterAgreement;
    },
    isEditAvailable() {
      if (!this.isBorrower) {
        return true;
      }

      return this.expiredCreditRegisterAgreement;
    },
    workInfoView() {
      return getWorkInfoView(this.profile?.work_info, this.$t.bind(this));
    },
    extraUserInfoView() {
      return getExtraUserInfoView(this.profile?.extra_user_info, this.$t.bind(this));
    }
  },
  methods: {
    goToEdit() {
      if (!this.isEditAvailable) {
        return;
      }

      const routeName = this.isBorrower
        ? 'edit-work-info-borrower'
        : 'edit-work-info-lender';

      this.$router.push({ name: routeName });
    }
  }
};
</script>

<style lang="scss" scoped>
.change-work-info-wrapper {
  @include page-wrapper-with-header-and-footer;

  padding-top: 20px;
  padding-bottom: 40px;
  box-sizing: border-box;
}

.change-work-info-container {
  width: 95%;
  min-height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.info-section {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: #FFFFFF;
  border: 1px solid #ECEDEE;
  box-shadow: 0 2px 8px rgba(1, 39, 59, 0.06);
}

.work-info-notice {
  width: 100%;
  padding: 16px 20px;
  box-sizing: border-box;
  border-radius: 12px;
  background-color: #FFDBDB;
  border: 1px solid #F5C2C2;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #C93434;
}

.back-block {
  width: fit-content;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.back-img {
  margin-right: 4px;
  height: 18px;
  width: 18px;
}

.back-btn-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #878787;
}

.section-title {
  margin-bottom: 20px;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #01273B;
}

.empty-text {
  font-size: 14px;
  line-height: 22px;
  color: #01273B;
}

.fields-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-label {
  font-size: 13px;
  line-height: 18px;
  color: #6B7A86;
}

.field-value {
  font-size: 14px;
  line-height: 22px;
  color: #01273B;
  word-break: break-word;
}

.edit-actions {
  margin-top: 50px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.edit-btn-container {
  width: fit-content;
  height: 38px;
}

.edit-btn-container ::v-deep .button {
  width: auto;
  min-width: auto;
  min-height: 38px;
  max-height: 38px;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
}

@media screen and (max-width: 600px) {
  .edit-actions {
    justify-content: stretch;
  }

  .edit-btn-container {
    width: 100%;
  }

  .edit-btn-container ::v-deep .button {
    width: 100%;
  }
}

.route-name {
  @include route-name;
  margin-bottom: 15px;
}

@media screen and (max-width: 1155px) {
  .change-work-info-wrapper {
    padding-top: 40px;
  }

  .change-work-info-container {
    @include base-page-container;

    align-items: flex-start;
    padding-top: 0;
  }
}
</style>
